import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { OrgVersionSelector } from '@client/components/obligations/OrgVersionSelector';
import { useQueryErrorEffect } from '@client/hooks/api/useQueryErrorEffect';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useIsUIDebugInfoEnabled } from '@client/hooks/system/useIsUIDebugInfoEnabled';
import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { ICONS } from '@client/styles/theme';
import { queryClient, trpc, trpcOptions } from '@client/trpc/client';
import { DETAIL_RECORD_PREFETCH_OPTIONS, OBLIGATION_RECORD_PREFETCH_OPTIONS } from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import { exportObligation } from '@client/utils';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { ORG_ROLES } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { Bot, Download } from 'lucide-react';
import { useState } from 'react';
import { match } from 'ts-pattern';
import {
    runDetailRoutePrefetchWhenAuthorised,
    shouldPrefetchDetailRouteData,
    yieldToPrefetchBatch,
} from '../../../detail-route-prefetch';
import { ObligationBreaches } from './ObligationBreaches';
import { obligationRecordInput, useObligationRecordQuery } from './obligation-record-query';
import { obligationFormStore } from './store';

export const Route = createFileRoute('/console/$orgId/compliance/obligations/details/$id')({
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    staticData: { ui: { widthVariant: 'default' }, entity: 'obligation' },
    beforeLoad: ({
        context: { trpcUtils },
        params,
        preload,
        abortController,
        cause,
    }): ShareableRouteContext | Promise<ShareableRouteContext> => {
        async function title() {
            const obligation = await trpcUtils.obligation.getObligation.ensureData(obligationRecordInput(params.id));
            return <span>Obligation Detail • {obligation.identificationCodeWithPrefix}</span>;
        }

        async function breadcrumb() {
            const obligation = await trpcUtils.obligation.getObligation.ensureData(obligationRecordInput(params.id));
            return `Obligation detail • ${obligation.identificationCodeWithPrefix}`;
        }

        const routeContext: ShareableRouteContext = {
            ui: {
                title,
            },
            breadcrumb: {
                title: breadcrumb,
            },
        };

        // Kick off the LCP-critical `getObligation` while the route's chunks are still being
        // parsed and the auth gate is still rendering — otherwise it cannot start until
        // ObligationDetails mounts. Same pattern as the claim, incident and complaint detail
        // routes; the gate mirrors the obligations layout (roles) and the compliance module.
        // On a cold deep link the session may still be restoring, so the prefetch is deferred
        // until the auth store becomes authorised. Hover-intent preloads never defer; leaving
        // the match aborts the deferral.
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: selectUser(authState)?.roles,
                    allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'compliance',
                });
            },
            () => prefetchObligationRecord(params.id, { preload }),
            { defer: !preload, signal: abortController.signal },
        );
        return cause === 'enter' ? yieldToPrefetchBatch().then(() => routeContext) : routeContext;
    },
    component: ObligationDetails,
});

/**
 * Warm the record ObligationDetails renders from, from the route's `beforeLoad`.
 *
 * - A navigation uses the mount observer's freshness window, so the observer joins the
 *   in-flight request instead of refetching on mount; a hover-intent preload uses the
 *   detail-record window, so repeated hovers over the same row do not resend it.
 * - Skipped while the record is already observed (the page is mounted for this obligation),
 *   as `beforeLoad` reruns for the retained match on every tab switch; the mounted observer
 *   owns freshness there.
 */
function prefetchObligationRecord(id: bigint, { preload }: { preload: boolean }): void {
    const options = trpcOptions.obligation.getObligation.queryOptions(
        obligationRecordInput(id),
        preload ? DETAIL_RECORD_PREFETCH_OPTIONS : OBLIGATION_RECORD_PREFETCH_OPTIONS,
    );
    if ((queryClient.getQueryCache().find({ queryKey: options.queryKey, exact: true })?.getObserversCount() ?? 0) > 0) {
        return;
    }
    void queryClient.prefetchQuery(options);
}

type ObligationBreachesAction = ['none'] | ['view', bigint];

function ObligationDetails() {
    const [obligationBreachesAction, setObligationBreachesAction] = useState<ObligationBreachesAction>(['none']);
    const updateObligationMutation = trpc.obligation.updateObligation.useMutation();
    const { trpcClient, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const { id } = Route.useParams();
    const isDebugInfoEnabled = useIsUIDebugInfoEnabled();

    const obligationQuery = useObligationRecordQuery(id);
    useQueryErrorEffect(obligationQuery.error, (error) => {
        match(error.data?.errorId)
            .with('recordNotFound', () => {
                void errorMessage
                    .showAsync(
                        "The obligation you are trying to access either doesn't exist or you don't have permissions to access it.",
                    )
                    .then(() => {
                        navigate({
                            to: '/console/$orgId/compliance/obligations',
                            params: { orgId },
                        });
                    });
            })
            .otherwise(() => {
                errorMessage.show(error);
            });
    });

    const { formData: obligation } = obligationFormStore.useInit({
        entity: 'obligation',
        data: obligationQuery.data,
        isLoading: obligationQuery.isLoading,

        async onReload() {
            await obligationQuery.refetch();
        },

        async onSave(currentValue, newValue) {
            try {
                await updateObligationMutation.mutateAsync({
                    id: currentValue.id,
                    set: newValue,
                });

                await trpcUtils.obligation.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    return (
        <PageController route={Route}>
            {obligationBreachesAction[0] === 'view' && (
                <OverlayPage
                    title="Breaches"
                    size="xLarge"
                    isDirty={() => false}
                    afterClose={() => setObligationBreachesAction(['none'])}
                >
                    <ObligationBreaches obligationId={obligationBreachesAction[1]} />
                </OverlayPage>
            )}

            {obligation && id === obligation.id ? (
                <>
                    <div className="mb-4">
                        <OrgVersionSelector
                            currentId={id}
                            currentVersion={obligation.version}
                            currentStatus={obligation.status}
                            identificationCode={obligation.identificationCode ?? null}
                            currentMasterObligationId={obligation.masterObligationId ?? null}
                        />
                    </div>
                    <Tabs
                        rightAddon={
                            <Button
                                type="secondary"
                                icon={<Download />}
                                onClick={() => void exportObligation(trpcClient, id)}
                            >
                                Download obligation
                            </Button>
                        }
                        primaryActionAddon={
                            <Button type="primary" onClick={() => setObligationBreachesAction(['view', obligation.id])}>
                                View breaches
                            </Button>
                        }
                        items={[
                            {
                                label: (
                                    <span className="flex items-center gap-2">
                                        <ICONS.manage size={16} />
                                        Manage obligation
                                    </span>
                                ),
                                to: '/console/$orgId/compliance/obligations/details/$id/manage',
                            },
                            ...(isDebugInfoEnabled
                                ? [
                                      {
                                          label: (
                                              <span className="flex items-center gap-2">
                                                  <Bot size={16} />
                                                  Debug (Embeddings)
                                              </span>
                                          ),
                                          to: '/console/$orgId/compliance/obligations/details/$id/debug' as const,
                                      },
                                  ]
                                : []),
                        ]}
                    />
                </>
            ) : (
                <Skeleton active />
            )}
        </PageController>
    );
}
